import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { addMonths } from 'date-fns'

import { getPositionName, hasAccessToThis } from 'services/Utilities'
import { fetchIssues } from 'services/Requests'
import { Format, Parse } from 'services/Dates'
import { destroy, init, setContent, setIsLoading } from 'store/actions/tables'
import { openModal } from 'store/actions/modals'
import { Card, DateTime, DateTimePicker, Table } from 'components/UI'
import { Alert, Input, Button as MerinioButton } from 'ui'
import BaseModal from 'components/Modals/BaseModal'

import { t } from 'translations'

const EmployeeScheduleIssuesModal = ({ userId, flags, start, end, dateValid, dateExpired }) => {
  const dispatch = useDispatch()

  const [[rangeStart, rangeEnd], setDates] = useState([
    Format(Parse(start || dateValid), { outFormat: 'date' }),
    !end && !dateExpired ? Format(addMonths(Parse(dateValid), 1), { outFormat: 'server' }) : Format(Parse(end || dateExpired), { outFormat: 'date' })
  ])

  const getIssues = useCallback(
    async e => {
      if (e) e.preventDefault()
      setIsLoading('employeeScheduleIssues', true)
      const shifts = await fetchIssues(Parse(rangeStart, { inFormat: 'date' }), Parse(rangeEnd, { inFormat: 'date' }), userId, flags)
      setContent('employeeScheduleIssues', shifts)
      setIsLoading('employeeScheduleIssues')
    },
    [flags, rangeEnd, rangeStart, userId]
  )

  useEffect(() => {
    getIssues()
  }, [getIssues])

  const rowActions = [
    hasAccessToThis('scheduleModify')
      ? ({ _id }) => (
          <MerinioButton
            type="button"
            color="secondary"
            size="small"
            onClick={() => dispatch(openModal('ScheduleEditModal', { _id, onModalClose: getIssues }))}
            outlined
          >
            {t('manager.scheduleIssues.EDIT_SHIFT')}
          </MerinioButton>
        )
      : null
  ]

  useEffect(() => {
    const header = [
      {
        key: '_id',
        label: 'manager.scheduleIssues.INFORMATIONS',
        type: 'custom',
        notRemovable: true,
        render: (none, { positionId, start, end }) => (
          <>
            <div>
              <b>{t('manager.scheduleIssues.DATE')}</b>
              <DateTime date={start} outFormat="humanDateFull" /> <DateTime date={[start, end]} range outFormat="time" />
            </div>
            <div>
              <b>{t('manager.scheduleIssues.POSITION')}</b>
              {getPositionName(positionId)}
            </div>
          </>
        )
      }
    ]

    init('employeeScheduleIssues', header, null, { content: [], count: 0 })
    return () => destroy('employeeScheduleIssues')
  }, [])

  const datePickerOptions = { wrap: true, mode: 'range', dateFormat: 'Y-m-d' }

  const body = (
    <>
      <Alert variant="danger" show={!rangeStart || !rangeEnd}>
        {t('manager.scheduleIssues.SELECT_DATE_RANGE')}
      </Alert>

      <DateTimePicker
        value={[rangeStart, rangeEnd]}
        onChange={([rangeStart, rangeEnd]) =>
          rangeStart && rangeEnd ? setDates([Format(rangeStart, { outFormat: 'date' }), Format(rangeEnd), { outFormat: 'date' }]) : null
        }
        options={datePickerOptions}
      >
        {' '}
        <div className="input-group">
          <Input type="text" placeholder={t('searchBar.SELECT_DATES')} data-input />
          <div className="input-group-append" id="button-addon4">
            <MerinioButton type="button" color="secondary" data-toggle outlined>
              <i className="fas fa-calendar-alt"></i>
            </MerinioButton>
            <MerinioButton type="button" color="secondary" data-clear outlined>
              <i className="fas fa-trash"></i>
            </MerinioButton>
          </div>
        </div>
      </DateTimePicker>
    </>
  )

  return (
    <BaseModal title={`components.scheduleIssues.${flags[0].toUpperCase()}`}>
      <Card body={body}>
        <Table name="employeeScheduleIssues" rowActions={rowActions} />
      </Card>
    </BaseModal>
  )
}

export default EmployeeScheduleIssuesModal
